import React, { useState, useEffect } from 'react';
import firebase from 'firebase/app';
import { ui } from 'util/ui';
import ArchyModal from 'components/shell/ArchyModal';
import { SearchIcon, MoveIcon } from 'components/shell/Icons';

/**
 * "Move file to another project" picker. UI refresh styling (Aaron, 5 Nov 2026): the shared
 * pop-up frame, a rounded search field and a list of project rows.
 */

type ProjectModalProps = {
  onClose: () => void;
  onSelect: (project: string) => void;
};

const ProjectModal: React.FC<ProjectModalProps> = ({ onClose, onSelect }) => {
  const [search, setSearch] = useState('');
  const [projects, setProjects] = useState<string[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      const snapshot = await firebase.firestore().collection('projects').get();
      const projectNames = snapshot.docs.map((doc) => doc.data().title).filter((t) => typeof t === 'string' && t.trim());
      setProjects(projectNames);
      setFilteredProjects(projectNames);
      setLoading(false);
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    const q = search.trim().toLowerCase();
    const filtered = projects.filter((project) => project.toLowerCase().includes(q));
    // Names starting with the search first, then alphabetical.
    filtered.sort((a, b) => {
      const sa = a.toLowerCase().indexOf(q) === 0 ? 0 : 1;
      const sb = b.toLowerCase().indexOf(q) === 0 ? 0 : 1;
      return sa - sb || a.localeCompare(b);
    });
    setFilteredProjects(filtered);
  }, [search, projects]);

  return (
    <ArchyModal title="Move to another project" subtitle="Choose the project this file should belong to." onClose={onClose} width={480}>
      <div style={{ padding: '14px 20px 10px', position: 'sticky', top: 0, background: ui.surface, zIndex: 1 }}>
        <label style={{ position: 'relative', display: 'block' }}>
          <span style={{ position: 'absolute', left: 12, top: 12, display: 'flex' }}>
            <SearchIcon size={16} />
          </span>
          <input
            type="text"
            autoFocus
            aria-label="Search projects"
            placeholder="Search projects"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 40,
              boxSizing: 'border-box',
              border: `1px solid ${ui.control}`,
              borderRadius: 10,
              padding: '0 12px 0 36px',
              fontSize: 14,
              color: ui.ink,
              background: ui.surface,
              outline: 'none',
            }}
          />
        </label>
      </div>
      <div style={{ padding: '0 12px 14px' }}>
        {loading ? (
          <p style={{ margin: 0, padding: '18px 8px', textAlign: 'center', color: ui.muted, fontSize: 14 }}>Loading projects…</p>
        ) : filteredProjects.length === 0 ? (
          <p style={{ margin: 0, padding: '18px 8px', textAlign: 'center', color: ui.muted, fontSize: 14 }}>No projects match "{search}".</p>
        ) : (
          filteredProjects.slice(0, 300).map((project) => (
            <button
              key={project}
              type="button"
              className="archy-row-btn"
              onClick={() => onSelect(project)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                textAlign: 'left',
                minHeight: 40,
                padding: '8px 10px',
                border: 'none',
                borderRadius: 10,
                background: 'transparent',
                font: 'inherit',
                fontSize: 14,
                color: ui.ink,
                cursor: 'pointer',
              }}
            >
              <MoveIcon size={16} color={ui.muted} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project}</span>
            </button>
          ))
        )}
        {filteredProjects.length > 300 && (
          <p style={{ margin: '6px 10px 0', fontSize: 12, color: ui.muted }}>Showing the first 300. Type to narrow the list.</p>
        )}
      </div>
    </ArchyModal>
  );
};

export default ProjectModal;
